// Refund-mode cart mutations.
//
// In refund mode the local cart and the BE refund receipt must stay in
// sync: Process Refund commits whatever's on the persisted receipt, so
// FE-only mutations (the regular sale path) would refund the original
// full amount regardless of what the cashier removed.
//
// This hook wraps the BE PUT/DELETE endpoints on receipt items and
// exposes refund-aware versions of `onQuantityChange` / `onRemoveFromCart`
// callbacks that:
//   1. Hit the BE so the persisted receipt matches the cashier's intent
//   2. Re-seed posStore.serverTotals from the response (refund totals
//      come back positive; reseedRefundTotalsFromReceipt flips signs)
//   3. Mirror the change into posStore.cart so the UI stays consistent
//      with the persisted state
//
// `isPending` reflects any in-flight refund-item mutation. The summary
// and the Process Refund button gate on it so the cashier can't process
// against stale totals.

import { useCallback, useEffect, useRef } from 'react'
import { useIsMutating } from '@tanstack/react-query'
import {
  useDeleteReceiptItem,
  useUpdateReceiptItem,
  receiptItemMutationKey,
} from '@/api/receipts'
import { v2ErrorText } from '@/api/v2'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'
import { reseedRefundTotalsFromReceipt } from '@/lib/refundBootstrap'
import type { CartItem } from '@/types'

// Refusal copy for the missing-receipt_item_id invariant break. Shared
// between the qty and remove paths so the cashier sees consistent
// wording if either fires.
const REFUND_LINE_UNLINKED_TITLE = 'Refund Line Not Linked'
const REFUND_LINE_UNLINKED_MESSAGE =
  'This line is missing its server reference. Cancel the refund and start it again so we can re-sync.'

// Both mutation paths roll the cart back to whatever the BE still
// believes, which on its own is a silent no-op from the cashier's side --
// they click X, the line stays, and nothing says why. The BE's refusals
// here are actionable (most often "cancel the recorded refund payment
// before changing the items", the lock that stops a tendered return from
// being emptied), so show what it said rather than a generic failure.
function showRefundItemError(err: unknown): void {
  useUIStore.getState().showToast({
    type: 'error',
    title: 'Refund Not Updated',
    message: v2ErrorText(
      err, 'Could not update this refund line. Please try again.',
    ),
  })
}

export interface UseRefundCartMutationsResult {
  // Replaces posStore.changeQuantity in refund mode. Delta is the change
  // to apply to the *signed* cart quantity (e.g. -1 on a refund line goes
  // from -2 -> -3 i.e. refund one more). The PUT body sends the signed
  // qty -- the BE stores refund items with NEGATIVE qty (set by
  // ops_lifecycle.copy_with_refund at qty=-src_item.qty), and the
  // amount_to_pay formula relies on that sign to land at zero after a
  // full refund. Passing Math.abs here positivizes the line and breaks
  // the totals (amount_to_pay flips to +2x the refund amount; FE then
  // refuses to dismiss the modal).
  onQuantityChange: (itemId: string, delta: number) => Promise<void>
  // Replaces posStore.removeFromCart in refund mode. Fires DELETE on the
  // persisted receipt item, then drops the local cart line. If the
  // receipt-item id is missing (upstream invariant break) the call
  // refuses with a visible error toast rather than mutating the cart;
  // local-only changes would diverge from CheckoutModal's id-keyed
  // reconciliation and let the cashier process a wrong refund total.
  onRemoveFromCart: (itemId: string) => Promise<void>
  // True while any refund-item PUT/DELETE is in flight. Gate UI on this
  // so totals don't display a stale figure during the round trip and the
  // Process Refund button can't fire against unconfirmed state.
  isPending: boolean
}

export function useRefundCartMutations(
  refundReceiptId: number | null,
): UseRefundCartMutationsResult {
  const updateReceiptItem = useUpdateReceiptItem()
  const deleteReceiptItem = useDeleteReceiptItem()

  // Synchronous per-itemId double-fire latch. The state-based gates
  // available here -- useMutation.isPending, useIsMutating, the
  // isRefundMutating posStore flag derived from them -- all settle a
  // render AFTER mutateAsync starts. A double-click on the X button
  // (or +/-) fires both click handlers in the same React tick before
  // any of those flags flip; both call mutateAsync; the BE sees two
  // identical DELETE/PUT requests ~50ms apart. (Observed in
  // production -- see the May 16 incident on receipt
  // S108R318-260516-00043: two v2_item_update entries at 11:52:24,924
  // and 11:52:24,976.)
  //
  // A ref read+write is synchronous, has no render lag, and beats
  // the click event rate. Keyed by itemId so a remove on item A
  // doesn't block a parallel +/- on item B. The latch is released in
  // `finally` so a transient mutation error doesn't permanently
  // wedge the row.
  //
  // The early-return guards (item not found, missing ids) bypass the
  // latch -- they're synchronous no-ops, no async work to gate.
  const pendingItemRef = useRef<Set<string>>(new Set())

  const onQuantityChange = useCallback(
    async (itemId: string, delta: number): Promise<void> => {
      const { cart, changeQuantity } = usePOSStore.getState()
      const item: CartItem | undefined = cart.find((c) => c.id === itemId)
      if (!item) return
      const receiptItemId = item.receipt_item_id ?? null

      // Pre-flight: refuse the edit when we can't address the BE line.
      // copy-with-refund always stamps receipt_item_id onto each cart
      // line (loadRefundItems threads it through), so a missing id
      // means an upstream invariant broke (BE deploy skew dropped the
      // field, hot-reload stripped the cart shape, etc.). The previous
      // behavior was a silent local-only edit that CheckoutModal then
      // dropped during reconciliation -- the cashier could process a
      // refund whose displayed total disagreed with the BE state. We
      // surface a visible toast and DO NOT mutate the local cart, so
      // the cart matches what's persisted and the cashier can void +
      // re-enter refund to re-sync.
      if (receiptItemId == null || refundReceiptId == null) {
        console.error('[refund] qty change without receipt_item_id', {
          itemId, receiptItemId, refundReceiptId,
        })
        useUIStore.getState().showToast({
          type: 'error',
          title: REFUND_LINE_UNLINKED_TITLE,
          message: REFUND_LINE_UNLINKED_MESSAGE,
        })
        return
      }

      // Synchronous latch acquired here -- AFTER the early returns
      // (no async work to gate) and BEFORE any state mutation. A
      // concurrent click on the same item sees the latch held and
      // bails before touching cart/BE.
      if (pendingItemRef.current.has(itemId)) return
      pendingItemRef.current.add(itemId)

      // Mirror the change locally first so the UI feels instant. The BE
      // response will re-seed totals; if the BE rejects (e.g. qty
      // exceeds source) we roll back below.
      const previousQty = item.quantity
      changeQuantity(itemId, delta)
      const newQty = previousQty + delta
      if (newQty === 0) {
        // Going through zero would imply removal — that's the X button's
        // job; the qty buttons cap at -1 / +1 short of zero in
        // RightSidebar.handleQuantityChange, so we shouldn't hit this in
        // practice. Bail without a BE call rather than emit qty=0.
        pendingItemRef.current.delete(itemId)
        return
      }

      try {
        const receipt = await updateReceiptItem.mutateAsync({
          receiptId: refundReceiptId,
          itemId: receiptItemId,
          data: { qty: newQty },
        })
        reseedRefundTotalsFromReceipt(
          receipt as unknown as {
            totals?: Record<string, unknown>
            amount_to_pay?: unknown
          },
        )
      } catch (err) {
        console.warn('[refund] PUT item failed; rolling back local qty', err)
        // Roll the local cart back to its pre-call state so the UI
        // matches what the BE still believes is on the receipt.
        usePOSStore.getState().changeQuantity(itemId, -delta)
        showRefundItemError(err)
      } finally {
        pendingItemRef.current.delete(itemId)
      }
    },
    [refundReceiptId, updateReceiptItem],
  )

  const onRemoveFromCart = useCallback(
    async (itemId: string): Promise<void> => {
      const { cart, removeFromCart } = usePOSStore.getState()
      const item: CartItem | undefined = cart.find((c) => c.id === itemId)
      if (!item) return
      const receiptItemId = item.receipt_item_id ?? null

      // Pre-flight refusal: same reasoning as the qty path. Removing
      // locally without a BE delete would leave the line on the
      // persisted receipt, and CheckoutModal's reconciliation skips
      // lines without receipt_item_id -- so Process Refund would
      // commit the original full amount while the cashier saw a
      // pruned cart. Surface an error and keep the cart untouched.
      if (receiptItemId == null || refundReceiptId == null) {
        console.error('[refund] remove without receipt_item_id', {
          itemId, receiptItemId, refundReceiptId,
        })
        useUIStore.getState().showToast({
          type: 'error',
          title: REFUND_LINE_UNLINKED_TITLE,
          message: REFUND_LINE_UNLINKED_MESSAGE,
        })
        return
      }

      // Synchronous latch -- prevents double-click DELETE storms.
      // Shares the keyspace with onQuantityChange so a tight +/- → X
      // sequence on the same row also serializes correctly (the
      // second action waits for the first to settle rather than
      // racing it).
      if (pendingItemRef.current.has(itemId)) return
      pendingItemRef.current.add(itemId)

      try {
        const receipt = await deleteReceiptItem.mutateAsync({
          receiptId: refundReceiptId,
          itemId: receiptItemId,
        })
        reseedRefundTotalsFromReceipt(
          receipt as unknown as {
            totals?: Record<string, unknown>
            amount_to_pay?: unknown
          },
        )
        removeFromCart(itemId)
      } catch (err) {
        console.warn('[refund] DELETE item failed; keeping local line', err)
        // Do NOT remove locally: the BE still has the line, so we keep
        // the FE consistent with that. Cashier can retry.
        showRefundItemError(err)
      } finally {
        pendingItemRef.current.delete(itemId)
      }
    },
    [refundReceiptId, deleteReceiptItem],
  )

  // useIsMutating sees every in-flight mutation under this key across
  // the whole tree, so ProductModal's own useUpdateReceiptItem /
  // useDeleteReceiptItem instances (refund-mode lot/qty edits) also
  // count -- the Process Refund gate would otherwise miss them. The
  // hook's local mutation.isPending fields only see this instance's
  // calls; the shared key + useIsMutating is what makes this bullet-
  // proof against the cashier opening ProductModal mid-refund.
  const inFlightCount = useIsMutating({ mutationKey: receiptItemMutationKey })
  const isPending = inFlightCount > 0

  // Mirror into posStore so CartSummary and CheckoutModal can gate on
  // pending state without prop-drilling. Only flag while in refund
  // mode -- the mutation key is shared with non-refund callers
  // (currently none, but future code could add one), and we don't
  // want a sale-mode item edit to disable the sale-mode checkout
  // button via the refund-mutating gate. exitRefundMode clears the
  // flag explicitly; this effect keeps it tight to the mutation
  // lifecycle while in refund mode.
  useEffect(() => {
    const shouldFlag = refundReceiptId != null && isPending
    usePOSStore.getState().setIsRefundMutating(shouldFlag)
  }, [isPending, refundReceiptId])

  return {
    onQuantityChange,
    onRemoveFromCart,
    isPending,
  }
}
